"use client"

// Reasoning: a collapsible panel for model "thinking" text, shown only when the
// stream carries it. Adapted from Vercel AI Elements
// (https://ai-sdk.dev/elements, reasoning), re-authored token-driven.

import { cn } from "@/lib/utils"
import { Minus, Plus } from "lucide-react"
import { useState, type ReactNode } from "react"
import reasoningIconAnimated from "@/assets/brand/reasoning-icon.gif"
import reasoningIconStatic from "@/assets/brand/reasoning.png"
import reasoningIconAnimatedWhite from "@/assets/brand/reasoning-white.gif"
import reasoningIconStaticWhite from "@/assets/brand/reasoning-icon-white.png"
import { useIsDark } from "@/components/theme/theme"
import { copy } from "@/lib/copy"

/**
 * Collapsible reasoning panel.
 *
 * @param children - the reasoning text content.
 * @param defaultOpen - whether the panel starts expanded.
 * @param stopped - whether the generation was stopped by the user.
 * @param streaming - whether the message is still streaming.
 * @param className - extra classes merged onto the container.
 */
export function Reasoning({
  children,
  defaultOpen = true,
  stopped = false,
  streaming = false,
  className,
}: {
  children: ReactNode
  defaultOpen?: boolean
  stopped?: boolean
  streaming?: boolean
  className?: string
}): ReactNode {
  const [open, setOpen] = useState<boolean>(defaultOpen)
  // Auto-collapse when the turn FINISHES streaming, so the final answer isn't
  // pushed down by the (now historical) reasoning. This adjusts state during
  // render by comparing against the previous `streaming` value — React's
  // recommended pattern over an Effect (no wasted extra render). We only act on
  // the true→false transition, so the user can freely re-open it afterwards
  // without it snapping shut again. A never-streamed message (reopened
  // transcript) starts collapsed via defaultOpen=false from the caller.
  const [prevStreaming, setPrevStreaming] = useState<boolean>(streaming)
  if (prevStreaming !== streaming) {
    setPrevStreaming(streaming)
    if (prevStreaming && !streaming) setOpen(false)
  }
  const isDark = useIsDark()
  const icon = streaming
    ? isDark
      ? reasoningIconAnimatedWhite
      : reasoningIconAnimated
    : isDark
      ? reasoningIconStaticWhite
      : reasoningIconStatic

  if (stopped) {
    return (
      <div className={cn("my-1.5 rounded-lg border border-composer-border bg-reasoning-stopped-bg dark:border-transparent", className)}>
        <div className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-muted-foreground">
          <img src={icon} alt="" aria-hidden="true" className="size-8 shrink-0" />
          <span className="flex-1 font-body font-medium">{copy.status.stopped}</span>
        </div>
      </div>
    )
  }

  return (
    <div className={cn("my-1.5 rounded-lg border", open ? "border-reasoning-open-border" : "border-composer-border dark:border-transparent", className)}>
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        aria-expanded={open}
        className={cn(
          "flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-muted-foreground transition-colors hover:bg-composer-bg/80 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring",
          open ? "rounded-t-lg bg-reasoning-open-bg dark:text-white" : "rounded-lg dark:bg-reasoning-open-bg"
        )}
      >
        <img src={icon} alt="" aria-hidden="true" className="size-8 shrink-0" />
        <span className="flex-1 font-body font-medium dark:font-normal dark:text-reasoning-title">{copy.status.reasoningTitle}</span>
        {open ? (
          <Minus className="size-4 shrink-0" aria-hidden="true" />
        ) : (
          <Plus className="size-4 shrink-0" aria-hidden="true" />
        )}
      </button>
      {open ? (
        <div className="border-t border-reasoning-open-border px-4 py-3 text-sm text-foreground/80 dark:bg-reasoning-open-bg">
          {children}
        </div>
      ) : null}
    </div>
  )
}
